import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, ListChecks, Lock, Unlock, CheckCircle2, Clock, AlertTriangle,
  ArrowLeft, ShieldCheck, MessageSquare, Send, Bell,
} from "lucide-react";
import { sr } from "../../lib/sr";

const CTYPES = [
  { v: "digital", label: "Digital" },
  { v: "radio", label: "Radio" },
  { v: "integrated", label: "Integrated (digital + radio)" },
];
const LEVELS = [
  { v: "self_service", label: "Self-service" },
  { v: "assisted", label: "AI-assisted + expert review" },
  { v: "full_service", label: "Full professional service" },
];
const STATUS_OPTS = [
  { v: "todo", label: "To do" },
  { v: "in_progress", label: "In progress" },
  { v: "blocked", label: "Blocked" },
  { v: "done", label: "Done" },
];
const STATUS_BADGE = { todo: "sr-badge-gray", in_progress: "sr-badge-amber", blocked: "sr-badge-red", done: "sr-badge-green" };
const AP_BADGE = { pending: "sr-badge-amber", approved: "sr-badge-green", rejected: "sr-badge-red", superseded: "sr-badge-gray" };

export default function CampaignProject() {
  const { cid } = useParams();
  const [data, setData] = useState(null);
  const [ctype, setCtype] = useState("digital");
  const [level, setLevel] = useState("assisted");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const [decisionComment, setDecisionComment] = useState("");
  const [people, setPeople] = useState([]);
  const [reminding, setReminding] = useState(null);

  const load = () => sr.campaignProject(cid).then(setData).catch(() => setData({ error: true }));
  useEffect(() => {
    load();
    sr.campaignAssignable(cid).then((r) => setPeople(r.people || [])).catch(() => {});
  }, [cid]);

  const setAssignee = async (tid, uid) => {
    try { const r = await sr.updateProjectTask(cid, tid, { assignee_id: uid }); setData(r); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };
  const remind = async (aid) => {
    setReminding(aid);
    try {
      const r = await sr.remindApproval(aid);
      toast.success(`Reminded ${r.reminded} approver${r.reminded === 1 ? "" : "s"}${r.emailed ? ` · ${r.emailed} emailed` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remind"); }
    finally { setReminding(null); }
  };

  const init = async () => {
    setBusy(true);
    try { await sr.initCampaignProject(cid, { campaign_type: ctype, service_level: level }); toast.success("Project workflow created"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not initialize"); }
    finally { setBusy(false); }
  };

  const setStatus = async (tid, status) => {
    try { const r = await sr.updateProjectTask(cid, tid, { status }); setData(r); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update task"); }
  };

  const submit = async () => {
    setBusy(true);
    try { const r = await sr.submitApproval(cid, { summary }); setData(r); setSummary(""); toast.success("Submitted for approval"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const decide = async (aid, decision) => {
    try { const r = await sr.decideApproval(aid, { decision, comment: decisionComment }); setData(r); setDecisionComment("");
      toast.success(decision === "approve" ? "Approved & locked" : "Sent back"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not record decision"); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load this campaign project.</p></div>;

  const { campaign, tasks, stages, approvals, progress } = data;
  const notInit = !campaign.campaign_type;
  const pending = (approvals || []).find((a) => a.status === "pending");
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div data-testid="campaign-project-page">
      <Link to="/app/campaigns" className="sr-muted" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
        <ArrowLeft size={14} /> Back to campaigns
      </Link>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="sr-badge" style={{ marginBottom: 8 }}><ListChecks size={12} /> Campaign project</span>
          <h1 style={{ fontSize: 28, textTransform: "capitalize" }}>{campaign.name}</h1>
          {!notInit && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              <span className="sr-badge sr-badge-gray" style={{ textTransform: "capitalize" }}>{campaign.campaign_type}</span>
              <span className="sr-badge sr-badge-gray">{(LEVELS.find((l) => l.v === campaign.service_level) || {}).label}</span>
              <span className="sr-badge sr-badge-amber" style={{ textTransform: "capitalize" }} data-testid="project-status">{(campaign.project_status || "planning").replace("_", " ")}</span>
              {campaign.locked
                ? <span className="sr-badge sr-badge-green" data-testid="project-locked"><Lock size={11} /> Locked (approved)</span>
                : <span className="sr-badge sr-badge-gray"><Unlock size={11} /> Open for edits</span>}
            </div>
          )}
        </div>
      </div>

      {notInit ? (
        <div className="sr-card" style={{ marginTop: 18 }} data-testid="project-init-card">
          <h3 style={{ fontSize: 16, color: "var(--navy)" }}>Set up this campaign project</h3>
          <p className="sr-muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 14 }}>
            Choose the type and service level — we will generate a workflow checklist you can tailor.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select className="sr-select" value={ctype} onChange={(e) => setCtype(e.target.value)} data-testid="init-type">
              {CTYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
            <select className="sr-select" value={level} onChange={(e) => setLevel(e.target.value)} data-testid="init-level">
              {LEVELS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
            </select>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={init} disabled={busy} data-testid="init-project-btn">
              {busy ? <Loader2 size={14} className="sr-spin" /> : <>Create workflow</>}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="sr-card" style={{ marginTop: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <strong style={{ color: "var(--navy)" }}>Workflow</strong>
              <span className="sr-muted" style={{ fontSize: 13 }} data-testid="project-progress">{progress.done}/{progress.total} done · {pct}%</span>
            </div>
            <div style={{ height: 8, background: "var(--blue-light)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "var(--blue)" }} />
            </div>
            {campaign.locked && (
              <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", color: "#067647", fontSize: 13, fontWeight: 600 }}>
                <Lock size={14} /> Approved and locked. Submit a new version below to make further edits.
              </div>
            )}
            {stages.map((stage) => {
              const st = tasks.filter((t) => t.stage === stage);
              if (!st.length) return null;
              return (
                <div key={stage} style={{ marginTop: 16 }}>
                  <div className="sr-muted" style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{stage}</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {st.map((t) => (
                      <div key={t.id} className="sr-card" data-testid={`task-${t.key}`}
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#fff" }}>
                        {t.status === "done" ? <CheckCircle2 size={16} style={{ color: "#067647", flexShrink: 0 }} />
                          : t.status === "blocked" ? <AlertTriangle size={16} style={{ color: "#C0362C", flexShrink: 0 }} />
                          : <Clock size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />}
                        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                          <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>{t.title}</div>
                          {t.description ? <div className="sr-muted" style={{ fontSize: 12 }}>{t.description}</div> : null}
                        </div>
                        <span className="sr-badge sr-badge-gray" style={{ fontSize: 10, flexShrink: 0 }}>{t.required_cap}</span>
                        <select className="sr-select" value={t.assignee_id || ""} disabled={campaign.locked}
                          onChange={(e) => setAssignee(t.id, e.target.value)} data-testid={`task-assignee-${t.key}`}
                          style={{ padding: "6px 8px", fontSize: 12, flex: "0 0 150px" }}>
                          <option value="">Unassigned</option>
                          {people.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
                        </select>
                        <select className="sr-select" value={t.status} disabled={campaign.locked}
                          onChange={(e) => setStatus(t.id, e.target.value)} data-testid={`task-status-${t.key}`}
                          style={{ padding: "6px 8px", fontSize: 12, flex: "0 0 130px" }}>
                          {STATUS_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="sr-card" style={{ marginTop: 18 }} data-testid="approvals-card">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <ShieldCheck size={16} style={{ color: "var(--blue)" }} />
              <strong style={{ color: "var(--navy)" }}>Approvals & sign-off</strong>
            </div>
            {!pending && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
                <input className="sr-input" placeholder="What is ready for sign-off?" value={summary}
                  onChange={(e) => setSummary(e.target.value)} data-testid="approval-summary" style={{ flex: 1, minWidth: 220 }} />
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={submit} disabled={busy} data-testid="submit-approval-btn">
                  {busy ? <Loader2 size={14} className="sr-spin" /> : <><Send size={14} /> Submit for approval</>}
                </button>
              </div>
            )}
            {(approvals || []).length === 0 ? (
              <p className="sr-muted" style={{ fontSize: 13 }}>No approvals yet. Submit the campaign for sign-off when it is ready.</p>
            ) : (
              <div style={{ display: "grid", gap: 10 }} data-testid="approvals-list">
                {approvals.map((a) => (
                  <div key={a.id} className="sr-card" data-testid={`approval-v${a.version}`} style={{ background: "#fff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <strong style={{ color: "var(--navy)" }}>Version {a.version}</strong>
                        <span className={`sr-badge ${AP_BADGE[a.status] || "sr-badge-gray"}`} style={{ textTransform: "capitalize" }}>{a.status}</span>
                        {a.status === "pending" && (
                          <span className={`sr-badge ${a.overdue ? "sr-badge-red" : "sr-badge-amber"}`} data-testid={`waiting-v${a.version}`}>
                            {a.overdue ? "Overdue · " : ""}waiting {Math.round(a.waiting_hours || 0)}h
                          </span>
                        )}
                      </div>
                      <span className="sr-muted" style={{ fontSize: 12 }}>by {a.submitted_by_email}</span>
                    </div>
                    {a.summary ? <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{a.summary}</p> : null}
                    {a.status === "pending" && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
                        <input className="sr-input" placeholder="Add a note (optional)" value={decisionComment}
                          onChange={(e) => setDecisionComment(e.target.value)} data-testid="decision-comment" style={{ flex: 1, minWidth: 180 }} />
                        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => decide(a.id, "approve")} data-testid={`approve-v${a.version}`}>
                          <CheckCircle2 size={14} /> Approve & lock
                        </button>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => decide(a.id, "reject")} data-testid={`reject-v${a.version}`}>
                          Send back
                        </button>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remind(a.id)} disabled={reminding === a.id} data-testid={`remind-v${a.version}`}>
                          {reminding === a.id ? <Loader2 size={14} className="sr-spin" /> : <><Bell size={14} /> Remind approver{a.reminder_count ? ` (${a.reminder_count})` : ""}</>}
                        </button>
                      </div>
                    )}
                    {a.decided_by_email && (
                      <div className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>
                        {a.status === "approved" ? "Approved" : "Decided"} by {a.decided_by_email}
                        {a.decision_comment ? ` — "${a.decision_comment}"` : ""}
                      </div>
                    )}
                    {(a.comments || []).length > 0 && (
                      <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 8, display: "grid", gap: 4 }}>
                        {a.comments.map((c, i) => (
                          <div key={i} className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 6 }}>
                            <MessageSquare size={12} /> <span><strong>{c.by_email}:</strong> {c.text}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
